import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText, Info, ArrowClockwise, Check, X, ShieldCheck, ListBullets, MagnifyingGlass, ArrowRight,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import {
  getbranchnames, getBiometricEmployeeCodes, getBiometricFinancialYears,
  getBiometricCalendarMonths, getBiometricEmployeeDetails,
  updateEmployeeBiometricAttendance, saveAuditLogData,
} from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { pickerOnly } from '../utils/pickerOnly';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

// NOTE: mirrors the leave-type list on the Angular screen — confirm against the
// real leave-type master before treating it as exhaustive.
const LEAVE_TYPES = [
  { label: 'Present', value: 'P' },
  { label: 'Absent', value: 'A' },
  { label: 'Casual Leave', value: 'CL' },
  { label: 'Sick Leave', value: 'SL' },
  { label: 'Extra Leave', value: 'XL' },
  { label: 'Holiday', value: 'H' },
  { label: 'Week Off', value: 'WO' },
];

// Keep whatever the API returned selectable even if it is outside the list.
function leaveTypeOptions(original) {
  if (!original || LEAVE_TYPES.some((t) => t.value === original)) return LEAVE_TYPES;
  return [{ label: original, value: original }, ...LEAVE_TYPES];
}

const MONTH_MAP = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function displayDate(value) {
  if (!value) return '—';
  return String(value).split('T')[0];
}

/** The attendance/payroll cycle a "MMM-YYYY" calendar month covers: the 24th of
 *  the month before through the 23rd of the month itself. NOV-2026 is therefore
 *  24 Oct 2026 → 23 Nov 2026.
 *
 *  Derived from the month the operator picked, not from today — the previous
 *  version computed one fixed range off the current date and reused it whatever
 *  was selected. Passing month index -1 or 12 to the Date constructor rolls the
 *  year over on its own, so December and January need no special case. */
function cycleRangeFor(calendarMonth) {
  if (!calendarMonth) return null;
  const [monStr, yearStr] = String(calendarMonth).split('-');
  const monthIdx = MONTH_MAP[(monStr || '').toUpperCase()];
  const year = parseInt(yearStr, 10);
  if (monthIdx === undefined || Number.isNaN(year)) return null;
  return {
    min: new Date(year, monthIdx - 1, 24),
    max: new Date(year, monthIdx, 23),
  };
}

/** Whether that cycle is the one running right now, which is what makes the date
 *  field usable. Deliberately a range test rather than a month-name match: on
 *  26 Aug the name still says AUG, but AUG's cycle closed on the 23rd and we are
 *  three days into SEP's. Matching on the name enabled the field for a month
 *  whose cycle was already over. */
function isCurrentCycle(calendarMonth) {
  const range = cycleRangeFor(calendarMonth);
  if (!range) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today >= range.min && today <= range.max;
}

const th = {
  textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB',
  color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap',
};
const td = { padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px' };

function EmpBiometricAttendance() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branches, setBranches] = useState([]);
  const [employeeCodes, setEmployeeCodes] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [calendarMonths, setCalendarMonths] = useState([]);

  const [branchCode, setBranchCode] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [financialYear, setFinancialYear] = useState('');
  const [calendarMonth, setCalendarMonth] = useState('');
  const [attendanceDate, setAttendanceDate] = useState('');
  const [reason, setReason] = useState('');

  const [details, setDetails] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingAudit, setPendingAudit] = useState([]);
  const [pendingBranchName, setPendingBranchName] = useState('');
  const [pendingEmployeeCode, setPendingEmployeeCode] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  // Picker bounds for whichever month is selected. The upper bound is clamped to
  // today so a date later than now can never be picked mid-cycle.
  const cycle = useMemo(() => {
    const range = cycleRangeFor(calendarMonth);
    if (!range) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { min: range.min, max: range.max > today ? today : range.max };
  }, [calendarMonth]);
  const dateEnabled = isCurrentCycle(calendarMonth);
  const selectedBranch = branches.find((b) => String(b.branchCode) === String(branchCode)) || null;

  useEffect(() => {
    getbranchnames()
      .then((res) => setBranches(Array.isArray(res) ? res : []))
      .catch(() => showToast('error', 'Load Failed', 'Failed to load branches. Please try again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const clearGrid = () => {
    setDetails([]);
    setShowGrid(false);
  };

  const handleBranchChange = async (value) => {
    setBranchCode(value || '');
    setEmployeeCode('');
    setFinancialYear('');
    setCalendarMonth('');
    setAttendanceDate('');
    setEmployeeCodes([]);
    setFinancialYears([]);
    setCalendarMonths([]);
    clearGrid();
    if (!value) return;

    try {
      const res = await getBiometricEmployeeCodes(value);
      setEmployeeCodes(Array.isArray(res) ? res : []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('[getBiometricEmployeeCodes] error =', err);
      showToast('error', 'Load Failed', 'Failed to load employee codes. Please try again.');
    }
  };

  const handleEmployeeChange = async (value) => {
    setEmployeeCode(value || '');
    setFinancialYear('');
    setCalendarMonth('');
    setAttendanceDate('');
    setFinancialYears([]);
    setCalendarMonths([]);
    clearGrid();
    if (!value || !branchCode) return;

    try {
      const res = await getBiometricFinancialYears(branchCode, value);
      setFinancialYears(Array.isArray(res) ? res : []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('[getBiometricFinancialYears] error =', err);
      showToast('error', 'Load Failed', 'Failed to load financial years. Please try again.');
    }
  };

  const handleFinancialYearChange = async (value) => {
    setFinancialYear(value || '');
    setCalendarMonth('');
    setAttendanceDate('');
    setCalendarMonths([]);
    clearGrid();
    if (!value || !branchCode || !employeeCode) return;

    try {
      const res = await getBiometricCalendarMonths(branchCode, employeeCode, value);
      setCalendarMonths(Array.isArray(res) ? res : []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('[getBiometricCalendarMonths] error =', err);
      showToast('error', 'Load Failed', 'Failed to load calendar months. Please try again.');
    }
  };

  const handleCalendarMonthChange = (value) => {
    setCalendarMonth(value || '');
    clearGrid();
    // The date is only meaningful for the month we're currently in, so it defaults
    // to today then and stays empty/disabled otherwise — same as the Angular screen.
    setAttendanceDate(isCurrentCycle(value) ? formatDate(new Date()) : '');
  };

  const loadDetails = async () => {
    if (!branchCode || !employeeCode || !financialYear || !calendarMonth || !attendanceDate) return;

    clearGrid();
    setLoadingDetails(true);
    try {
      const res = await getBiometricEmployeeDetails(
        branchCode, employeeCode, financialYear, calendarMonth, attendanceDate,
      );
      const rows = (Array.isArray(res) ? res : []).map((d) => ({
        branchName: d.branchName,
        employeeCode: d.employeeCode,
        attendanceDate: d.attendanceDate,
        employeeName: d.employeeName,
        leaveType: d.leaveType,
        originalLeaveType: d.leaveType,
        noOfAvailableCasualLeaves: d.noOfAvailableCasualLeaves,
        noOfAvailableSickLeaves: d.noOfAvailableSickLeaves,
      }));
      setDetails(rows);
      if (isEmptyResult(rows)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      setShowGrid(true);
    } catch (err) {
      console.error('[getBiometricEmployeeDetails] error =', err);
      setShowGrid(true); // show the "no records" message, same as the Angular screen
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to load attendance details.'));
    } finally {
      setLoadingDetails(false);
    }
  };

  const onLeaveTypeChange = (index, value) => {
    setDetails((rows) => rows.map((r, i) => (i === index ? { ...r, leaveType: value } : r)));
  };

  const changedRows = useMemo(
    () => details.filter((d) => (d.leaveType ?? '') !== (d.originalLeaveType ?? '')),
    [details],
  );

  const branchInvalid = touched && !branchCode;
  const employeeInvalid = touched && !employeeCode;
  const yearInvalid = touched && !financialYear;
  const monthInvalid = touched && !calendarMonth;
  const dateInvalid = touched && !attendanceDate;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!branchCode && !!employeeCode && !!financialYear && !!calendarMonth
    && !!attendanceDate && reason.trim().length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (changedRows.length === 0) {
      showToast('warn', 'No Changes', 'Please change at least one leave type before saving.');
      return;
    }

    if (changedRows.some((r) => !r.leaveType)) {
      showToast('warn', 'Leave Type Required', 'Select a leave type for every row you cleared.');
      return;
    }

    const trimmedReason = reason.trim();

    setPendingBranchName(selectedBranch?.branchName ?? branchCode);
    setPendingEmployeeCode(employeeCode);
    setPendingReason(trimmedReason);
    setPendingRows(changedRows);

    // One audit entry per changed row, matching the Angular screen.
    setPendingAudit(changedRows.map((r) => ({
      schemaName: branchCode,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'EMP_BIOMETRIC_LEAVE_TYPE_CHANGE',
      oldData: { employeeCode: r.employeeCode, attendanceDate: r.attendanceDate, leaveType: r.originalLeaveType },
      newData: { employeeCode: r.employeeCode, attendanceDate: r.attendanceDate, leaveType: r.leaveType },
      reason: trimmedReason,
    })));

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingRows([]);
    setPendingAudit([]);
  };

  const confirmSave = async () => {
    if (pendingRows.length === 0) return;

    setSaving(true);
    try {
      // UpdateEmployeeBiometricAttendance swallows its own exceptions into the
      // returned string and still answers 200, so check every response body.
      const results = await Promise.all(pendingRows.map((row) => updateEmployeeBiometricAttendance(
        branchCode, row.employeeCode ?? '', row.leaveType ?? '', row.attendanceDate ?? '',
      )));

      const failure = results.find((r) => !String(r ?? '').toLowerCase().includes('success'));
      if (failure !== undefined) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('UpdateEmployeeBiometricAttendance reported a failure:', failure);
        showToast('error', 'Update Failed', String(failure) || 'Could not update the leave type. Please try again.', true);
        return;
      }

      try {
        await saveAuditLogData(pendingAudit);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Leave type updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Leave type updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('UpdateEmployeeBiometricAttendance error:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update the leave type. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranchCode('');
    setEmployeeCode('');
    setFinancialYear('');
    setCalendarMonth('');
    setAttendanceDate('');
    setEmployeeCodes([]);
    setFinancialYears([]);
    setCalendarMonths([]);
    clearGrid();
    setReason('');
    setTouched(false);
    setPendingRows([]);
    setPendingAudit([]);
  };

  const leaveLabel = (value) => LEAVE_TYPES.find((t) => t.value === value)?.label || value || '—';

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="emp-biometric-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="emp-biometric-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Leave Change Request</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Employee Biometric Attendance</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Reference */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Reference</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, employee, financial year, month and date, then click Get</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Branch Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`branch-${formKey}`}
              options={branches}
              getLabel={(o) => o.branchName}
              getValue={(o) => o.branchCode}
              value={branchCode}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={branchInvalid}
              testId="emp-biometric-branch"
            />
            {branchInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Employee Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`employee-${formKey}`}
              options={employeeCodes}
              getLabel={(o) => o.employee_code}
              getValue={(o) => o.employee_code}
              value={employeeCode}
              onChange={handleEmployeeChange}
              placeholder="Select employee code"
              emptyMessage="Select a branch first"
              disabled={!branchCode}
              error={employeeInvalid}
              testId="emp-biometric-employee"
            />
            {employeeInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Employee code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Financial Year <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`year-${formKey}`}
              options={financialYears}
              getLabel={(o) => o.financial_year}
              getValue={(o) => o.financial_year}
              value={financialYear}
              onChange={handleFinancialYearChange}
              placeholder="Select financial year"
              emptyMessage="Select an employee first"
              disabled={!employeeCode}
              error={yearInvalid}
              testId="emp-biometric-year"
            />
            {yearInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Financial year is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Calendar Month <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`month-${formKey}`}
              options={calendarMonths}
              getLabel={(o) => o.calendar_month}
              getValue={(o) => o.calendar_month}
              value={calendarMonth}
              onChange={handleCalendarMonthChange}
              placeholder="Select calendar month"
              emptyMessage="Select a financial year first"
              disabled={!financialYear}
              error={monthInvalid}
              testId="emp-biometric-month"
            />
            {monthInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Calendar month is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Date <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              type="date"
              className="input-field st-datepicker"
              style={{ fontSize: '13px', borderColor: dateInvalid ? '#DC2626' : undefined }}
              value={attendanceDate}
              min={cycle ? formatDate(cycle.min) : undefined}
              max={cycle ? formatDate(cycle.max) : undefined}
              disabled={!dateEnabled}
              onChange={(e) => { setAttendanceDate(e.target.value); clearGrid(); }}
              {...pickerOnly({ disabled: !dateEnabled })}
              data-testid="emp-biometric-date"
            />
            {dateInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Date is required</div>}
            {!dateEnabled ? (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                {cycle
                  ? `${calendarMonth} covers ${formatDate(cycleRangeFor(calendarMonth).min)} to ${formatDate(cycleRangeFor(calendarMonth).max)} — that cycle is closed, so the date cannot be changed`
                  : 'Select the current calendar month to enable date selection'}
              </div>
            ) : (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                {calendarMonth} cycle: {formatDate(cycle.min)} to {formatDate(cycle.max)}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '12px' }}>
          <button
            type="button"
            className="btn-primary"
            disabled={!attendanceDate || loadingDetails}
            onClick={loadDetails}
            data-testid="emp-biometric-get-button"
          >
            <MagnifyingGlass size={14} style={{ marginRight: '4px' }} /> {loadingDetails ? 'Loading…' : 'Get'}
          </button>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!attendanceDate} hint="Select a date in section A first">
        {/* SECTION B: Attendance Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Attendance Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Change the leave type below, then provide a reason</div>
          </div>
        </div>

        {showGrid && details.length > 0 && (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }} data-testid="emp-biometric-grid">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
              <ListBullets size={14} /> Attendance Details
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>
                {details.length} record(s)
              </span>
              {changedRows.length > 0 && (
                <span style={{ fontSize: '10px', fontWeight: '700', color: '#065F46', background: '#ECFDF5', padding: '2px 6px', borderRadius: '4px' }}>
                  {changedRows.length} changed
                </span>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Branch</th>
                    <th style={th}>Emp. Code</th>
                    <th style={th}>Date</th>
                    <th style={th}>Emp. Name</th>
                    <th style={{ ...th, width: '180px' }}>Leave Type</th>
                    <th style={{ ...th, textAlign: 'right' }}>Available CL&apos;s</th>
                    <th style={{ ...th, textAlign: 'right' }}>Available SL&apos;s</th>
                  </tr>
                </thead>
                <tbody>
                  {details.map((d, i) => {
                    const changed = (d.leaveType ?? '') !== (d.originalLeaveType ?? '');
                    return (
                      <tr key={`${d.employeeCode}-${d.attendanceDate}-${i}`} style={{ background: changed ? '#FFFBEB' : 'transparent' }}>
                        <td style={td}>{d.branchName ?? '—'}</td>
                        <td style={td}>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>
                            {d.employeeCode}
                          </span>
                        </td>
                        <td style={td}>{displayDate(d.attendanceDate)}</td>
                        <td style={td}>{d.employeeName ?? '—'}</td>
                        <td style={td}>
                          <SearchableSelect
                            options={leaveTypeOptions(d.originalLeaveType)}
                            value={d.leaveType ?? ''}
                            onChange={(value) => onLeaveTypeChange(i, value)}
                            getLabel={(t) => t.label}
                            getValue={(t) => t.value}
                            placeholder="Search leave type..."
                            error={changed && !d.leaveType}
                            testId="emp-biometric-leave-type"
                          />
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{Number(d.noOfAvailableCasualLeaves ?? 0).toFixed(2)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{Number(d.noOfAvailableSickLeaves ?? 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showGrid && details.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', border: '1px solid #C7D2FE', background: '#F1F5FF', borderRadius: '8px', fontSize: '12px', color: '#334155' }} data-testid="emp-biometric-empty">
            <Info size={14} /> No records found for the selected date.
          </div>
        )}

        {!showGrid && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px', border: '1px dashed #E5E7EB', borderRadius: '8px', color: '#9CA3AF', fontSize: '12px' }}>
            <Info size={16} /> Pick a date and click Get to load the attendance records
          </div>
        )}
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!attendanceDate || !showGrid || details.length === 0} hint="Load attendance records in section B first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Explain why this leave type is being changed..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="emp-biometric-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : 'Reason must be at least 10 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="emp-biometric-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving || changedRows.length === 0} data-testid="emp-biometric-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '480px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto' }}
            data-testid="emp-biometric-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Leave Type Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to change the leave type for <strong>{pendingRows.length}</strong> record(s) of employee{' '}
                <strong>{pendingEmployeeCode}</strong> at <strong>{pendingBranchName}</strong>. Please review before confirming.
              </p>

              {pendingRows.map((row, i) => (
                <div key={`${row.employeeCode}-${row.attendanceDate}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{displayDate(row.attendanceDate)} — Old</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{leaveLabel(row.originalLeaveType)}</p>
                  </div>
                  <ArrowRight size={14} color="#9CA3AF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New</p>
                    <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{leaveLabel(row.leaveType)}</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="emp-biometric-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="emp-biometric-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmpBiometricAttendance;
